import React from 'react'
import { kbControlsLayerDefault, useKeyboardControls } from '../../../contexts/KeyboardControlsContext'
import { HighlightEffect } from '../../../enums/KeyboardControls'
import { ControlHighlight, KeyboardControl } from '../../molecules/KeyboardControls'
import toggleOnSvg from '../../../assets/images/buttons/btn-stdntmenu-toggle-on.svg'
import toggleOffSvg from '../../../assets/images/buttons/btn-stdntmenu-toggle-off.svg'

interface ToggleButtonProps {
  id: string
  label: string
  kbControlsLayer?: string
  children: React.ReactNode
  isOn: boolean
  onClick: () => void
  variant?: 'default' | 'accessibility'
  ariaLive?: 'off' | 'polite' | 'assertive'
}

const ToggleButton: React.FC<ToggleButtonProps> = ({
  id,
  label,
  kbControlsLayer = kbControlsLayerDefault,
  children,
  isOn,
  onClick,
  variant = 'default',
  ariaLive = 'polite'
}: ToggleButtonProps) => {
  const keyboardControls = useKeyboardControls()

  const fullId = `toggle-button-${id}`

  if (variant === 'accessibility') {
    return (
      <div className='relative'>
        <ControlHighlight
          active={keyboardControls.isEnabled && keyboardControls.isElementFocused(fullId)}
          highlightEffect={HighlightEffect.ButtonHexOutline}
          className='w-full'
        >
          <KeyboardControl layer={kbControlsLayer}>
            <button
              id={`${fullId}`}
              data-testid={`${fullId}`}
              onClick={onClick}
              aria-label={`${label}: ${isOn ? 'On' : 'Off'}`}
              aria-live={ariaLive}
              className='w-full'
            >
              <div className='flex flex-row w-full'>
                <span className='w-full text-left text-dopage-accessibilityHighlight'>
                  {children}
                </span>
                <div className='flex items-center justify-center ml-auto'>
                  <img
                    src={isOn ? toggleOnSvg : toggleOffSvg}
                    alt={isOn ? 'Toggle On' : 'Toggle Off'}
                    data-testid='toggle-container'
                    className='cursor-pointer'
                  />
                </div>
              </div>
            </button>
          </KeyboardControl>
        </ControlHighlight>
      </div>
    )
  }

  return (
    <div>
      <ControlHighlight
        active={keyboardControls.isEnabled && keyboardControls.isElementFocused(fullId)}
        highlightEffect={HighlightEffect.ButtonHexOutline}
        className='w-[432px]'
      >
        <KeyboardControl layer={kbControlsLayer}>
          <button
            id={`${fullId}`}
            data-testid={`${fullId}`}
            onClick={onClick}
            aria-label={`${label}: ${isOn ? 'On' : 'Off'}`}
            aria-live={ariaLive}
          >
            <div className='flex flex-row rounded-lg border-dopage-lightGreen border-[3px] p-[12px] w-[432px]'>
              <span className='w-full text-left'>
                {children}
              </span>
              <div className='flex items-center justify-center bg-dopage-black ml-auto'>
                <img
                  src={isOn ? toggleOnSvg : toggleOffSvg}
                  alt={isOn ? 'Toggle On' : 'Toggle Off'}
                  data-testid='toggle-container'
                  className='cursor-pointer'
                />
              </div>
            </div>
          </button>
        </KeyboardControl>
      </ControlHighlight>
    </div>
  )
}

export default ToggleButton
